import { createContext, useContext, useCallback, useEffect, useState } from "react";
import camApi from "@/lib/camApi";

// Shared CAM state: the caller's permissions + lookup maps (categories, locations).
// Loaded once when the CAM section mounts; pages read from here instead of re-fetching.
const CamCtx = createContext(null);

export function CamProvider({ children }) {
  const [context, setContext] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tree, setTree] = useState({ campuses: [], buildings: [], floors: [], rooms: [], labs: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadLookups = useCallback(async () => {
    const [cats, t] = await Promise.all([
      camApi.categories().then((r) => r.categories || []).catch(() => []),
      camApi.tree().catch(() => ({})),
    ]);
    setCategories(cats);
    setTree({
      campuses: t.campuses || [], buildings: t.buildings || [], floors: t.floors || [],
      rooms: t.rooms || [], labs: t.labs || [], departments: t.departments || [],
    });
  }, []);

  const reloadContext = useCallback(async () => {
    try {
      const r = await camApi.context();
      setContext(r.context);
      return r.context;
    } catch (e) { setError(e); return null; }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      // Fire all three together instead of context-then-lookups. For no-access users
      // the lookup calls 403 and are caught below, so parallelising is safe and saves
      // a full round-trip on every CAM page load.
      const [ctx, cats, t] = await Promise.all([
        camApi.context().then((r) => r.context).catch((e) => { if (alive) setError(e); return null; }),
        camApi.categories().then((r) => r.categories || []).catch(() => []),
        camApi.tree().catch(() => ({})),
      ]);
      if (!alive) return;
      if (ctx) setContext(ctx);
      setCategories(cats);
      setTree({
        campuses: t.campuses || [], buildings: t.buildings || [], floors: t.floors || [],
        rooms: t.rooms || [], labs: t.labs || [], departments: t.departments || [],
      });
      setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  // Handy label lookups
  const catName = useCallback((id) => categories.find((c) => c.id === id)?.name || "—", [categories]);
  const labName = useCallback((id) => tree.labs.find((l) => l.id === id)?.name || null, [tree]);
  const roomName = useCallback((id) => tree.rooms.find((r) => r.id === id)?.name || null, [tree]);
  const deptName = useCallback((id) => tree.departments.find((d) => d.id === id)?.name || null, [tree]);

  const value = {
    context, categories, tree, loading, error,
    reloadContext, reloadLookups: loadLookups,
    catName, labName, roomName, deptName,
    can: {
      admin: !!context?.cam_admin,
      store: !!context?.is_store,
      maintenance: !!context?.is_maintenance,
    },
  };
  return <CamCtx.Provider value={value}>{children}</CamCtx.Provider>;
}

export function useCam() {
  const v = useContext(CamCtx);
  if (!v) throw new Error("useCam outside CamProvider");
  return v;
}
